/**
 * The Alpha Desk — six workspaces with tabs, in place of the thirty-odd
 * separate pages (see the transition notes of 18 Oct 2026).
 *
 * Pure config, read by the sidebar, the router, the desk page and the agent's
 * screen catalogue. Every tab is one of three kinds:
 *   - `live`     the screen moved here, with its data;
 *   - `handoff`  the screen still lives on its old page; the tab carries the
 *                context (client, portfolio, account) across to it;
 *   - `planned`  a new tab that waits on a backend item (`needs`).
 *
 * The URL is `/desk/{workspace}/{tab}` with the context in the query string —
 * symbol, portfolio, client, account, horizon, asof, view, cad — so any view
 * can be linked, bookmarked or opened by the agent.
 */

export const DESK_BASE = '/desk';

/** The query params that travel between tabs and workspaces. */
export const DESK_CONTEXT = ['symbol', 'portfolio', 'client', 'account', 'horizon', 'asof', 'view', 'cad'];

/** The review strip's editions, as the `cad` param spells them. */
export const REVIEW_CADENCES = [
  { id: 'morning', label: 'This morning' },
  { id: 'week', label: 'This week' },
  { id: 'month', label: 'This month' },
];

/** The impact switch: the book as it stands, or with the pending trades applied. */
export const IMPACT_VIEWS = [
  { id: 'now', label: 'Now' },
  { id: 'after', label: 'With pending trades' },
];

export const WORKSPACES = [
  {
    id: 'ideas',
    key: 'deskIdeas',
    label: 'Ideas',
    icon: 'home',
    question: 'What does the model like today?',
    side: 'find',
    tabs: [
      { id: 'today', label: 'Today', kind: 'live', blurb: 'The desk in one glance: what changed overnight, the morning read, and every screen one step away.' },
      { id: 'ranks', label: 'Ranked ideas', kind: 'live', blurb: 'The ranked universe at one horizon, with the factor story in one line per name.' },
      { id: 'statement', label: 'Statement → trade', kind: 'planned', blurb: 'Any statement — a view, a headline, a client’s question — turned into sized ideas.', needs: 'the statement-to-ideas service' },
      { id: 'screener', label: 'Screener', kind: 'live', blurb: 'Every saved research screen, its parameters and the names it matched.' },
      { id: 'quant', label: 'Quant lab', kind: 'planned', blurb: 'Quant signals tried against the universe before they become factors.', needs: 'the signal lab service' },
    ],
  },
  {
    id: 'stock',
    key: 'deskStock',
    label: 'Stock',
    icon: 'research',
    question: 'Everything the platform knows about one name.',
    side: 'find',
    tabs: [
      { id: 'overview', label: 'Overview & plan', kind: 'live', blurb: 'The recommendation, price discipline, upside, your book and the alpha term structure.' },
      { id: 'alpha', label: 'Alpha & factors', kind: 'live', blurb: 'Factor drivers by family; open a factor for the raw data, formula and inputs behind it.' },
      { id: 'fundamentals', label: 'Fundamentals', kind: 'live', blurb: 'The full master record: fields, statements, earnings, estimates, peers, similar and diversifying names.' },
      { id: 'valuation', label: 'Valuation', kind: 'live', blurb: 'Monte-Carlo p10–p90 against price, P(value > price), implied growth and ROIC, scenarios.' },
      { id: 'thesis', label: 'Research & thesis', kind: 'live', blurb: 'The analyst panel, personas, and the written, evidence-linked thesis with its review path.' },
      { id: 'report', label: 'Research report', kind: 'live', blurb: 'The initiating-coverage report in full, with every version and a new one on request.' },
      { id: 'agents', label: 'Agents & reviews', kind: 'live', blurb: 'What the agents say about this name, and the assistant to ask for more.' },
    ],
  },
  {
    id: 'research',
    key: 'deskResearch',
    label: 'Research',
    icon: 'insights',
    question: 'Is the factor, is the model, any good?',
    side: 'find',
    tabs: [
      { id: 'factors', label: 'Factor library', kind: 'live', blurb: 'The catalogue: definition, formula, inputs and candidate weight per horizon.' },
      { id: 'generate', label: 'Generate factors', kind: 'planned', blurb: 'New factors proposed from the data and the literature, ready for a backtest.', needs: 'the factor-generation service' },
      { id: 'backtest', label: 'Factor backtest', kind: 'planned', blurb: 'One factor on its own: IC by horizon, decile spread, decay and half-life.', needs: 'backend B15–B17' },
      { id: 'validation', label: 'Validation & gates', kind: 'live', blurb: 'Walk-forward folds, every factor against its gates, and the gate board.' },
      { id: 'registry', label: 'Registry & experiments', kind: 'live', blurb: 'The experiment registry and the model registry: model card and backtests per version.' },
      { id: 'health', label: 'Model health & data', kind: 'live', blurb: 'Nightly jobs, alerts with acknowledge, and every monitor against its band.' },
    ],
  },
  {
    id: 'books',
    key: 'deskBooks',
    label: 'Stock models',
    icon: 'mandates',
    question: 'Does the book earn it?',
    side: 'hold',
    tabs: [
      { id: 'books', label: 'Model books', kind: 'live', blurb: 'The mandate, its diagnostics, holdings with alpha and risk contribution, and the last rebalance.' },
      { id: 'optimiser', label: 'Exposure optimiser', kind: 'live', blurb: 'Pick the stocks, set the criteria, run — every run a dry run on the server.' },
      { id: 'paper', label: 'Paper trading', kind: 'live', blurb: 'Paper against its twin and the benchmark: drag, fills, weekly reports, halt and resume.' },
      { id: 'mine', label: 'My portfolios', kind: 'live', blurb: 'Saved books traded on paper every day: NAV, return split, drift, rebalance history.' },
    ],
  },
  {
    id: 'portfolio',
    key: 'deskPortfolio',
    label: 'Portfolio',
    icon: 'analyzer',
    question: 'One client’s money, read in full.',
    side: 'hold',
    tabs: [
      { id: 'overview', label: 'Overview', kind: 'live', blurb: 'The client’s portfolios, what needs action, and the way into each.' },
      { id: 'holdings', label: 'Holdings', kind: 'handoff', blurb: 'Look-through holdings, timelapse and the positions behind every figure.', to: { screen: 'portfolios', params: { tab: 'holdings' } } },
      { id: 'pnl', label: 'P&L & attribution', kind: 'handoff', blurb: 'Return and factor attribution, Brinson, and the stored contribution of each holding.', to: { screen: 'portfolios', params: { tab: 'returns' } } },
      { id: 'risk', label: 'Exposures & risk', kind: 'handoff', blurb: 'Realised and fundamental risk, regression factors, exposures by sector and style.', to: { screen: 'portfolios', params: { tab: 'risk' } } },
      { id: 'scen', label: 'Scenarios & sensitivities', kind: 'handoff', blurb: 'Stress tests, scenario P&L and the sensitivities behind them.', to: { screen: 'portfolios', params: { tab: 'scenarios' } } },
      { id: 'goals', label: 'Goals, policy & trading rules', kind: 'handoff', blurb: 'The goal plan, allocation against policy bands, the IPS, and each trading account’s rules.', to: { screen: 'goals', params: {} } },
      { id: 'rebalance', label: 'Rebalance proposal', kind: 'handoff', blurb: 'The trade simulator’s impact, the 5-year backtest, saved proposals and placing the legs.', to: { screen: 'proposals', params: {} } },
      { id: 'activity', label: 'Activity & reports', kind: 'handoff', blurb: 'SIPs and flows, the timeline, and the reports written for this client.', to: { screen: 'client360', params: { tab: 'timeline' } } },
    ],
  },
  {
    id: 'trade',
    key: 'deskTrade',
    label: 'Trade',
    icon: 'txn',
    question: 'Size it, place it, watch it.',
    side: 'hold',
    tabs: [
      { id: 'orders', label: 'Orders & fills', kind: 'live', blurb: 'Every order on the client’s trading accounts and how it filled.' },
      { id: 'newtrade', label: 'New trade', kind: 'live', blurb: 'An idea sized at three risk levels inside the account’s limits.' },
      { id: 'intraday', label: 'Intraday', kind: 'live', blurb: 'The intraday account: its rules, limit usage and today’s ideas.' },
      { id: 'shortterm', label: 'Short-term', kind: 'live', blurb: 'The swing account: its rules, limit usage and ideas for the week.' },
      { id: 'positions', label: 'Positions & P&L', kind: 'live', blurb: 'Holdings with level charts: average buy, supports, resistances, stop and targets.' },
      { id: 'accounts', label: 'Accounts & review', kind: 'live', blurb: 'Account summary, what needs attention, deposits and withdrawals.' },
    ],
  },
];

export const WORKSPACE_IDS = WORKSPACES.map((w) => w.id);

/** A workspace by its id, or null. */
export const workspaceOf = (id) => WORKSPACES.find((w) => w.id === id) ?? null;

/** A workspace by its ROUTES key, or null. */
export const workspaceByKey = (key) => WORKSPACES.find((w) => w.key === key) ?? null;

/** The tab ids of a workspace. */
export const tabIdsOf = (ws) => (ws?.tabs ?? []).map((t) => t.id);

/** `/desk/stock/overview?symbol=…` — the search is a query string, URLSearchParams or object. */
export function deskPath(workspace, tab, search) {
  const ws = typeof workspace === 'string' ? workspaceOf(workspace) : workspace;
  if (!ws) return DESK_BASE;
  const t = tab && tabIdsOf(ws).includes(tab) ? tab : ws.tabs[0].id;
  let q = '';
  if (search instanceof URLSearchParams) q = search.toString();
  else if (search && typeof search === 'object') {
    const p = new URLSearchParams();
    Object.entries(search).forEach(([k, v]) => { if (v != null && v !== '') p.set(k, String(v)); });
    q = p.toString();
  } else if (typeof search === 'string') q = search.replace(/^\?/, '');
  q = q.replace(/%2C/gi, ',');
  return `${DESK_BASE}/${ws.id}/${t}${q ? `?${q}` : ''}`;
}

/**
 * Whether the old pages redirect into the desk. The switch the transition
 * notes ask for: the old routes keep working behind it until each workspace
 * passes its parity checklist, then they go.
 */
export const DESK_REDIRECTS_ON = true;

/* ── Old routes → the desk, with their context mapped ───────────────────── */

/** Old query names → the desk's. Anything not listed travels under its own name. */
export const LEGACY_PARAM = {
  sym: 'symbol', sasof: 'asof', sh: 'horizon', rid: 'report', fac: 'factor', goal: 'account',
};

/** Each stock-alpha screen id → the workspace and tab it became. */
export const LEGACY_SCREEN_TAB = {
  today: ['ideas', 'today'],
  ranks: ['ideas', 'ranks'],
  why: ['stock', 'overview'],
  report: ['stock', 'report'],
  books: ['books', 'books'],
  theses: ['stock', 'thesis'],
  paper: ['books', 'paper'],
  health: ['research', 'health'],
  factors: ['research', 'factors'],
  research: ['research', 'validation'],
  optimiser: ['books', 'optimiser'],
  mine: ['books', 'mine'],
};

/** Carries a query string across, renaming the keys the desk spells differently. */
function carry(search, extra = {}) {
  const from = new URLSearchParams(String(search || '').replace(/^\?/, ''));
  const to = new URLSearchParams();
  from.forEach((v, k) => { if (v !== '') to.set(LEGACY_PARAM[k] || k, v); });
  Object.entries(extra).forEach(([k, v]) => { if (v != null && v !== '') to.set(k, String(v)); });
  return to;
}

/**
 * Where an old URL lands in the desk, or null when it is not one the desk
 * replaces. `/stock-alpha/why?sym=X` → `/desk/stock/overview?symbol=X`,
 * `/stocks/X?tab=risk` → `/desk/stock/fundamentals?symbol=X&section=risk`.
 */
export function legacyDeskTarget(pathname, search) {
  const path = String(pathname || '').replace(/\/+$/, '');
  let m = /^\/stock-alpha(?:\/([^/]+))?$/.exec(path);
  if (m) {
    const [ws, tab] = LEGACY_SCREEN_TAB[m[1] || 'today'] || LEGACY_SCREEN_TAB.today;
    return deskPath(ws, tab, carry(search));
  }
  m = /^\/stocks\/([^/]+)$/.exec(path);
  if (m) {
    const q = new URLSearchParams(String(search || '').replace(/^\?/, ''));
    const section = q.get('tab');
    q.delete('tab');
    let symbol = m[1];
    try { symbol = decodeURIComponent(symbol); } catch { /* keep as typed */ }
    return deskPath('stock', 'fundamentals', carry(q, { symbol, section }));
  }
  return null;
}
